import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { format } from "date-fns";
import {
  ArrowLeft, ArrowRight, Calendar as CalendarIcon, Clock, MapPin,
  Users, Check, X as XIcon, Sparkles, Package as PackageIcon, ImageOff,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { useSeo } from "@/hooks/useSeo";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface Tour {
  id: string; slug: string; title: string;
  short_description: string | null; long_description: string | null;
  main_image_url: string | null; gallery: any;
  duration: string | null; max_capacity: number | null;
  adult_price: number | null; child_price: number | null;
  pricing_mode: string | null;
  destination_id: string | null;
  highlights: any; includes: any; not_includes: any;
  what_to_bring: any; restrictions: any; what_to_expect: string | null;
  available_schedules: any; is_active: boolean;
}
interface Pkg { id: string; name: string; description: string | null; price: number; max_persons: number | null; sort_order: number | null; }
interface Destination { id: string; slug: string; title: string; }

const arr = (v: any): string[] => (Array.isArray(v) ? v.filter(Boolean).map(String) : []);

const TourDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [tour, setTour] = useState<Tour | null>(null);
  const [packages, setPackages] = useState<Pkg[]>([]);
  const [destination, setDestination] = useState<Destination | null>(null);
  const [related, setRelated] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(true);

  // Booking state
  const [date, setDate] = useState<Date | undefined>();
  const [schedule, setSchedule] = useState<string | undefined>();
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [packageId, setPackageId] = useState<string | undefined>();

  useSeo({
    title: tour ? `${tour.title} | Allen Travels RD` : "Tour | Allen Travels RD",
    description: tour?.short_description ?? undefined,
  });

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    supabase.from("tours").select("*").eq("slug", slug).eq("is_active", true).maybeSingle()
      .then(async ({ data }) => {
        const t = data as Tour | null;
        setTour(t);
        if (!t) { setLoading(false); return; }
        const [pkgRes, destRes, relRes] = await Promise.all([
          supabase.from("tour_packages").select("*").eq("tour_id", t.id).order("sort_order"),
          t.destination_id
            ? supabase.from("destinations").select("id,slug,title").eq("id", t.destination_id).maybeSingle()
            : Promise.resolve({ data: null }),
          supabase.from("tours").select("id,slug,title,main_image_url,duration,adult_price,destination_id,pricing_mode,short_description,long_description,gallery,max_capacity,child_price,highlights,includes,not_includes,what_to_bring,restrictions,what_to_expect,available_schedules,is_active,is_featured")
            .eq("is_active", true).neq("id", t.id).limit(3),
        ]);
        setPackages((pkgRes.data as Pkg[]) || []);
        setDestination((destRes.data as Destination | null) || null);
        setRelated((relRes.data as Tour[]) || []);
        setLoading(false);
      });
  }, [slug]);

  const schedules = useMemo(() => arr(tour?.available_schedules), [tour]);
  const gallery = useMemo(() => {
    const g = arr(tour?.gallery);
    return g.length ? g : tour?.main_image_url ? [tour.main_image_url] : [];
  }, [tour]);

  const selectedPkg = packages.find((p) => p.id === packageId);
  const isPackages = tour?.pricing_mode === "packages";
  const total = isPackages
    ? (selectedPkg?.price ?? 0)
    : (adults * (tour?.adult_price ?? 0)) + (children * (tour?.child_price ?? 0));

  const handleBook = () => {
    if (!tour) return;
    if (!date) { toast.error("Please select a date."); return; }
    if (schedules.length > 0 && !schedule) { toast.error("Please select a schedule."); return; }
    if (isPackages && !selectedPkg) { toast.error("Please select a package."); return; }
    if (!isPackages && adults < 1) { toast.error("At least 1 adult is required."); return; }

    navigate("/tour-booking", {
      state: {
        tourId: tour.id,
        tourSlug: tour.slug,
        tourTitle: tour.title,
        tourImage: tour.main_image_url,
        destinationTitle: destination?.title || "",
        pricingMode: tour.pricing_mode || "fixed",
        adultPrice: tour.adult_price,
        childPrice: tour.child_price,
        adults: isPackages ? 1 : adults,
        children: isPackages ? 0 : children,
        selectedPackageId: selectedPkg?.id ?? null,
        selectedPackageName: selectedPkg?.name ?? null,
        selectedPackagePrice: selectedPkg?.price ?? null,
        excursionDate: date.toISOString(),
        schedule: schedule ?? null,
        totalPrice: total,
        duration: tour.duration,
      },
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-32 container mx-auto px-4">
          <div className="h-[60vh] rounded-3xl bg-muted animate-pulse" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!tour) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center text-center px-4">
          <div>
            <h1 className="font-display text-3xl font-semibold">Tour not found</h1>
            <p className="text-muted-foreground mt-2">It may have been moved or is no longer available.</p>
            <Button className="mt-6" onClick={() => navigate("/tours")}>Browse all tours</Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-28">
        <div className="absolute inset-0 -z-10">
          {tour.main_image_url ? (
            <img src={tour.main_image_url} alt={tour.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-hero" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--hero-bg))]/70 via-[hsl(var(--hero-bg))]/40 to-[hsl(var(--hero-bg))]/95" />
        </div>

        <div className="container mx-auto px-4 pt-16 pb-32 text-white">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white transition-smooth"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="max-w-3xl mt-6">
            {destination && (
              <Link
                to={`/destinations/${destination.slug}`}
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-primary-glow font-semibold"
              >
                <MapPin className="w-3.5 h-3.5" />
                {destination.title}
              </Link>
            )}
            <h1 className="font-display font-semibold text-4xl md:text-6xl leading-[1.05] mt-4">
              {tour.title}
            </h1>
            {tour.short_description && (
              <p className="mt-5 text-lg text-white/85 max-w-2xl">{tour.short_description}</p>
            )}
            <div className="mt-6 flex flex-wrap gap-3">
              {tour.duration && (
                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-dark text-xs font-medium">
                  <Clock className="w-3.5 h-3.5 text-primary-glow" /> {tour.duration}
                </span>
              )}
              {tour.max_capacity && (
                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-dark text-xs font-medium">
                  <Users className="w-3.5 h-3.5 text-primary-glow" /> Up to {tour.max_capacity}
                </span>
              )}
              {tour.adult_price ? (
                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent text-accent-foreground text-xs font-semibold">
                  <Sparkles className="w-3.5 h-3.5" /> From ${tour.adult_price}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="container mx-auto px-4 -mt-20 pb-24">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* LEFT */}
          <div className="lg:col-span-2 space-y-10">
            {/* Gallery */}
            {gallery.length > 0 && (
              <div className="grid grid-cols-4 gap-3">
                {gallery.slice(0, 4).map((src, i) => (
                  <Dialog key={i}>
                    <DialogTrigger asChild>
                      <button
                        className={cn(
                          "relative overflow-hidden rounded-2xl group bg-muted",
                          i === 0 ? "col-span-4 md:col-span-2 md:row-span-2 aspect-square" : "col-span-2 md:col-span-1 aspect-square",
                        )}
                      >
                        <img src={src} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-smooth duration-700" />
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-5xl p-0 bg-transparent border-none">
                      <img src={src} alt="" className="w-full h-auto rounded-xl" />
                    </DialogContent>
                  </Dialog>
                ))}
              </div>
            )}

            {/* Tabs */}
            <div className="bg-card rounded-3xl shadow-card border border-border/60 p-6 md:p-8">
              <Tabs defaultValue="overview">
                <TabsList className="bg-muted/60 rounded-full p-1 flex-wrap h-auto">
                  <TabsTrigger value="overview" className="rounded-full">Overview</TabsTrigger>
                  {arr(tour.highlights).length > 0 && <TabsTrigger value="highlights" className="rounded-full">Highlights</TabsTrigger>}
                  {(arr(tour.includes).length > 0 || arr(tour.not_includes).length > 0) && (
                    <TabsTrigger value="includes" className="rounded-full">Includes</TabsTrigger>
                  )}
                  {arr(tour.what_to_bring).length > 0 && <TabsTrigger value="bring" className="rounded-full">What to bring</TabsTrigger>}
                  {arr(tour.restrictions).length > 0 && <TabsTrigger value="restrictions" className="rounded-full">Restrictions</TabsTrigger>}
                  {tour.what_to_expect && <TabsTrigger value="expect" className="rounded-full">What to expect</TabsTrigger>}
                </TabsList>

                <TabsContent value="overview" className="pt-6 prose prose-neutral max-w-none">
                  <p className="text-foreground/85 leading-relaxed whitespace-pre-line">
                    {tour.long_description || tour.short_description || "An unforgettable Caribbean experience."}
                  </p>
                </TabsContent>

                <TabsContent value="highlights" className="pt-6">
                  <ul className="grid md:grid-cols-2 gap-3">
                    {arr(tour.highlights).map((h, i) => (
                      <li key={i} className="flex items-start gap-3"><Sparkles className="w-4 h-4 text-primary mt-1 shrink-0" /><span>{h}</span></li>
                    ))}
                  </ul>
                </TabsContent>

                <TabsContent value="includes" className="pt-6 grid md:grid-cols-2 gap-8">
                  {arr(tour.includes).length > 0 && (
                    <div>
                      <h4 className="font-display font-semibold mb-3">Included</h4>
                      <ul className="space-y-2">
                        {arr(tour.includes).map((h, i) => (
                          <li key={i} className="flex items-start gap-2.5"><Check className="w-4 h-4 text-primary mt-1 shrink-0" /><span className="text-sm">{h}</span></li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {arr(tour.not_includes).length > 0 && (
                    <div>
                      <h4 className="font-display font-semibold mb-3">Not included</h4>
                      <ul className="space-y-2">
                        {arr(tour.not_includes).map((h, i) => (
                          <li key={i} className="flex items-start gap-2.5"><XIcon className="w-4 h-4 text-destructive mt-1 shrink-0" /><span className="text-sm">{h}</span></li>
                        ))}
                      </ul>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="bring" className="pt-6">
                  <ul className="grid md:grid-cols-2 gap-3">
                    {arr(tour.what_to_bring).map((h, i) => <li key={i} className="flex items-start gap-2"><Check className="w-4 h-4 text-primary mt-1" /><span>{h}</span></li>)}
                  </ul>
                </TabsContent>

                <TabsContent value="restrictions" className="pt-6">
                  <ul className="space-y-2">
                    {arr(tour.restrictions).map((h, i) => <li key={i} className="flex items-start gap-2"><XIcon className="w-4 h-4 text-destructive mt-1" /><span>{h}</span></li>)}
                  </ul>
                </TabsContent>

                <TabsContent value="expect" className="pt-6">
                  <p className="text-foreground/85 leading-relaxed whitespace-pre-line">{tour.what_to_expect}</p>
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* RIGHT — Booking card */}
          <div className="lg:col-span-1">
            <div className="sticky top-28 bg-gradient-card rounded-3xl shadow-elegant border border-border/60 p-6 space-y-5">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Reserve</div>
                <h3 className="font-display text-2xl font-semibold mt-1">Book this tour</h3>
              </div>

              <div className="space-y-2">
                <Label>Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start font-normal h-11 rounded-xl">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {date ? format(date, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} />
                  </PopoverContent>
                </Popover>
              </div>

              {schedules.length > 0 && (
                <div className="space-y-2">
                  <Label>Schedule</Label>
                  <Select value={schedule} onValueChange={setSchedule}>
                    <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Select schedule" /></SelectTrigger>
                    <SelectContent>
                      {schedules.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {isPackages ? (
                <div className="space-y-2">
                  <Label>Package</Label>
                  {packages.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No packages available.</p>
                  ) : (
                    <div className="space-y-2">
                      {packages.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => setPackageId(p.id)}
                          className={cn(
                            "w-full text-left rounded-xl border p-3 transition-smooth",
                            packageId === p.id
                              ? "border-primary bg-primary/5 shadow-soft"
                              : "border-border hover:border-primary/40",
                          )}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="font-semibold flex items-center gap-2">
                                <PackageIcon className="w-4 h-4 text-primary" />
                                {p.name}
                              </div>
                              {p.description && <div className="text-xs text-muted-foreground mt-1">{p.description}</div>}
                            </div>
                            <div className="font-display font-semibold text-primary">${p.price}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Adults</Label>
                    <Input type="number" min={1} value={adults} onChange={(e) => setAdults(Math.max(1, parseInt(e.target.value || "1")))} className="h-11 rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <Label>Children</Label>
                    <Input type="number" min={0} value={children} onChange={(e) => setChildren(Math.max(0, parseInt(e.target.value || "0")))} className="h-11 rounded-xl" />
                  </div>
                </div>
              )}

              <div className="flex items-end justify-between pt-4 border-t border-border">
                <span className="text-sm text-muted-foreground">Total</span>
                <span className="font-display text-3xl font-semibold text-gradient-ocean">${total || 0}</span>
              </div>

              <Button onClick={handleBook} size="lg" className="w-full rounded-full bg-gradient-ocean text-primary-foreground shadow-glow hover:-translate-y-0.5 transition-smooth font-semibold">
                Continue to booking
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-20">
            <h2 className="font-display text-3xl font-semibold mb-8">You may also like</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link key={r.id} to={`/tours/${r.slug}`} className="group relative overflow-hidden rounded-3xl bg-card shadow-card hover:shadow-elegant transition-smooth">
                  <div className="aspect-[4/5] overflow-hidden bg-muted">
                    {r.main_image_url ? (
                      <img src={r.main_image_url} alt={r.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-smooth duration-700" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground"><ImageOff className="w-8 h-8" /></div>
                    )}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--hero-bg))]/90 via-transparent to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                    <h3 className="font-display text-xl font-semibold">{r.title}</h3>
                    {r.adult_price ? <div className="text-primary-glow text-sm mt-1">From ${r.adult_price}</div> : null}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default TourDetailPage;
